import { Candle, Tick } from '@shared/types';
import { useMarketStore } from '../store/marketStore';
import { usePaperTradingStore } from '../store/paperTradingStore';
import { isMarketOpen } from '../utils/marketHours';
import { paperTradingAPI } from './PaperTradingAPI';
import { getPreferences, updatePreferenceDirect } from '../hooks/usePreferences';

export type PlaybackSpeed = 0.5 | 1 | 2 | 5 | 10 | 50 | 100 | 500 | 1000 | 2000 | 5000;
export type PlaybackState = 'playing' | 'paused' | 'stopped' | 'completed';

interface BacktestPlayerCallbacks {
  onProgress?: (current: number, total: number, currentTime: number) => void;
  onStateChange?: (state: PlaybackState) => void;
  onComplete?: () => void;
}

/**
 * BacktestPlayer - Simulates tick-by-tick playback of historical data
 * Feeds ticks incrementally and builds candles in real-time
 */
export class BacktestPlayer {
  private allTicks: Tick[] = [];
  private currentIndex: number = 0;
  private state: PlaybackState = 'stopped';
  private speed: PlaybackSpeed = 1;
  private intervalId: NodeJS.Timeout | null = null;
  private callbacks: BacktestPlayerCallbacks = {};

  // Candle building state
  private barInterval: number = 2; // minutes
  private currentCandle: Candle | null = null;
  private currentBarTimestamp: number = 0;

  // Store last generated candles for component remounting
  private lastGeneratedCandles: Candle[] = [];

  // Throttling to prevent overwhelming UI with updates
  private skipUIUpdates: boolean = false;

  // Backtest session tracking
  private currentSessionId: string | null = null;

  constructor(callbacks?: BacktestPlayerCallbacks) {
    this.callbacks = callbacks || {};
  }

  /**
   * Update callbacks
   */
  setCallbacks(callbacks: BacktestPlayerCallbacks): void {
    this.callbacks = { ...this.callbacks, ...callbacks };
  }

  /**
   * Load ticks for playback
   */
  async loadTicks(ticks: Tick[], barInterval: number = 2): Promise<void> {
    console.log(`🎬 [BACKTEST] loadTicks called with ${ticks.length} ticks`);

    // Validate tick data structure
    if (ticks.length > 0) {
      const firstTick = ticks[0];
      console.log(`🔍 [BACKTEST] First tick structure:`, {
        timestamp: firstTick.timestamp,
        timestampType: typeof firstTick.timestamp,
        price: firstTick.price,
        priceType: typeof firstTick.price,
        volume: firstTick.volume,
        volumeType: typeof firstTick.volume,
        isDate: (firstTick.timestamp as unknown) instanceof Date,
        raw: firstTick
      });

      // Check for invalid timestamps
      const invalidTicks = ticks.filter(t =>
        typeof t.timestamp !== 'number' ||
        isNaN(t.timestamp) ||
        (t.timestamp as unknown) instanceof Date
      );

      if (invalidTicks.length > 0) {
        console.error(`❌ [BACKTEST] Found ${invalidTicks.length} ticks with invalid timestamps:`, invalidTicks.slice(0, 3));
        throw new Error(`Invalid tick data: ${invalidTicks.length} ticks have invalid timestamps`);
      }
    }

    this.allTicks = [...ticks].sort((a, b) => a.timestamp - b.timestamp);
    this.currentIndex = 0;
    this.state = 'stopped';
    this.barInterval = barInterval;
    this.currentCandle = null;
    this.currentBarTimestamp = 0;

    // ==================== Clean Slate: Create New Backtest Session ====================

    // Complete previous session if exists
    if (this.currentSessionId) {
      try {
        await paperTradingAPI.completeBacktestSession(this.currentSessionId);
        console.log(`✅ [BACKTEST] Completed previous session: ${this.currentSessionId}`);
      } catch (error) {
        console.warn(`⚠️ [BACKTEST] Failed to complete previous session:`, error);
      }
    }

    // Create new backtest session
    try {
      const marketStore = useMarketStore.getState();
      const startTimestamp = this.allTicks[0]?.timestamp || Date.now();
      const endTimestamp = this.allTicks[this.allTicks.length - 1]?.timestamp || Date.now();

      const sessionMetadata = await paperTradingAPI.createBacktestSession({
        name: `Backtest ${new Date(startTimestamp).toLocaleDateString()}`,
        symbol: marketStore.symbol,
        startDate: startTimestamp,
        endDate: endTimestamp,
      });

      this.currentSessionId = sessionMetadata.id;
      console.log(`🎬 [BACKTEST] Created new session: ${this.currentSessionId}`);

      // Reset paper trading state to CLEAN SLATE
      const paperStore = usePaperTradingStore.getState();
      paperStore.resetAccount();
      paperStore.setBacktestMode(true, this.currentSessionId);

      console.log(`🔄 [BACKTEST] Reset to clean slate - Balance: $${paperStore.balance.toLocaleString()}`);
    } catch (error) {
      console.error(`❌ [BACKTEST] Failed to create session:`, error);
      // Continue without session tracking if API fails
      this.currentSessionId = null;
    }

    console.log(`✅ [BACKTEST] Loaded ${this.allTicks.length.toLocaleString()} ticks for playback (${barInterval}m bars)`);

    // Only auto-seek if we don't already have candles (first load)
    // This preserves the seek position when navigating between tabs
    if (this.lastGeneratedCandles.length === 0) {
      // Automatically seek to 6:00 AM PST to start backtest from market open
      // PST is UTC-8, so 6:00 AM PST = 14:00 UTC
      // We need to find the first tick on or after 6:00 AM PST on the start date
      let targetIndex = 0;

      // Find the first tick at or after 6:00 AM PST
      for (let i = 0; i < this.allTicks.length; i++) {
        const tickDate = new Date(this.allTicks[i].timestamp);
        const utcHour = tickDate.getUTCHours();
        const utcMinute = tickDate.getUTCMinutes();

        // Convert UTC to PST (UTC-8)
        // 6:00 AM PST = 14:00 UTC (6 + 8)
        // Find first tick at or after 14:00 UTC
        const isAfter6AmPST = utcHour > 14 || (utcHour === 14 && utcMinute >= 0);

        if (isAfter6AmPST) {
          targetIndex = i;
          const pstDate = new Date(tickDate.getTime() - (8 * 60 * 60 * 1000)); // Convert to PST for display
          console.log(`🔄 [BACKTEST] Found 6:00 AM PST at index ${targetIndex.toLocaleString()} (UTC: ${tickDate.toISOString()}, PST: ${pstDate.toLocaleString('en-US', { timeZone: 'America/Los_Angeles' })})`);
          break;
        }
      }

      // If no tick found at or after 6 AM, use first tick
      if (targetIndex === 0 && this.allTicks.length > 0) {
        const firstTickDate = new Date(this.allTicks[0].timestamp);
        console.log(`⚠️ [BACKTEST] No tick found at/after 6:00 AM PST, using first tick at ${firstTickDate.toISOString()}`);
      }

      console.log(`🔄 [BACKTEST] Auto-seeking to 6:00 AM PST (index ${targetIndex.toLocaleString()})...`);
      this.seekToIndex(targetIndex).then(() => {
        console.log(`✅ [BACKTEST] Auto-seek complete - chart should now display ${this.lastGeneratedCandles.length} candles`);
      });
    } else {
      console.log(`ℹ️  [BACKTEST] Skipping auto-seek - already have ${this.lastGeneratedCandles.length} candles (preserving position)`);

      // Restore the seekbar position from saved currentIndex
      // This preserves the exact seekbar position across tab switches
      const prefs = getPreferences();
      const savedIndex = prefs.backtestSeekbarIndex;
      if (savedIndex !== undefined) {
        try {
          const targetIndex = savedIndex;
          if (targetIndex >= 0 && targetIndex < this.allTicks.length) {
            console.log(`🔄 [BACKTEST] Restoring seekbar position: index ${targetIndex.toLocaleString()} (${((targetIndex / this.allTicks.length) * 100).toFixed(1)}%)`);
            // Update currentIndex to sync seekbar (don't need to regenerate candles)
            this.currentIndex = targetIndex;

            // Fire progress callback to update UI
            if (this.callbacks.onProgress) {
              this.callbacks.onProgress(this.currentIndex, this.allTicks.length, this.allTicks[this.currentIndex].timestamp);
            }
          }
        } catch (e) {
          console.error('Failed to restore seekbar position:', e);
        }
      }

      // Restore playback state to prevent play() from resetting to index 0
      // If we're not at the end, restore to 'paused' state (prevents reset in play())
      const savedState = prefs.backtestPlaybackState;
      if (savedState && (savedState === 'paused' || savedState === 'stopped')) {
        this.state = savedState as PlaybackState;
        console.log(`🔄 [BACKTEST] Restored playback state: ${this.state}`);
      } else if (this.currentIndex < this.allTicks.length - 1) {
        // If no saved state but we're not at the end, set to paused
        this.state = 'paused';
        console.log(`🔄 [BACKTEST] Set playback state to 'paused' (not at end)`);
      }

      // CRITICAL FIX: Restore currentCandle and currentBarTimestamp from the restored position
      // Without this, feedNextCandle() will create a new candle without proper context
      // and the chart will lose all historical candles when play() is clicked
      if (this.currentIndex > 0 && this.currentIndex < this.allTicks.length) {
        const currentTick = this.allTicks[this.currentIndex];
        const intervalMs = this.barInterval * 60 * 1000;
        this.currentBarTimestamp = Math.floor(currentTick.timestamp / intervalMs) * intervalMs;

        // Find the current candle from lastGeneratedCandles
        this.currentCandle = this.lastGeneratedCandles.find(c => c.timestamp === this.currentBarTimestamp) || null;

        if (this.currentCandle) {
          console.log(`🔄 [BACKTEST] Restored current candle state: timestamp=${new Date(this.currentBarTimestamp).toISOString()}`);
        } else {
          console.warn(`⚠️ [BACKTEST] Could not find current candle for timestamp ${new Date(this.currentBarTimestamp).toISOString()}`);
        }
      }

      // CRITICAL FIX: Restore candles to marketStore
      // When switching tabs, the backtest is reloaded and marketStore.setCandles([]) is called
      // which clears all candles. We need to restore them from lastGeneratedCandles.
      if (this.lastGeneratedCandles.length > 0) {
        console.log(`🔄 [BACKTEST] Restoring ${this.lastGeneratedCandles.length} candles to marketStore`);
        const marketStore = useMarketStore.getState();
        marketStore.setCandles(this.lastGeneratedCandles);
      }
    }
  }

  /**
   * Start playback from current position
   */
  play(): void {
    if (this.allTicks.length === 0) {
      console.warn('No ticks loaded for playback');
      return;
    }

    if (this.state === 'playing') {
      console.warn('Already playing');
      return;
    }

    if (this.state === 'completed') {
      // Restart from beginning
      this.currentIndex = 0;
      this.currentCandle = null;
      this.currentBarTimestamp = 0;
    }

    this.state = 'playing';
    updatePreferenceDirect('backtestPlaybackState', this.state);
    this.callbacks.onStateChange?.(this.state);
    console.log(`▶️ [BACKTEST] Starting playback at tick ${this.currentIndex}/${this.allTicks.length} (speed: ${this.speed}x)`);

    this.startPlayback();
  }

  /**
   * Pause playback
   */
  pause(): void {
    if (this.state !== 'playing') {
      return;
    }

    this.state = 'paused';
    updatePreferenceDirect('backtestPlaybackState', this.state);
    this.callbacks.onStateChange?.(this.state);
    console.log(`⏸️ [BACKTEST] Paused at index ${this.currentIndex}/${this.allTicks.length}`);

    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  /**
   * Stop playback and reset to beginning
   */
  async stop(): Promise<void> {
    this.state = 'stopped';
    updatePreferenceDirect('backtestPlaybackState', this.state);
    this.currentIndex = 0;
    this.callbacks.onStateChange?.(this.state);
    console.log(`⏹️ [BACKTEST] Stopped playback`);

    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }

    // Clear chart data
    const marketStore = useMarketStore.getState();
    marketStore.setCandles([]);

    // Complete session and exit backtest mode
    if (this.currentSessionId) {
      try {
        await paperTradingAPI.completeBacktestSession(this.currentSessionId);
        console.log(`✅ [BACKTEST] Completed session: ${this.currentSessionId}`);
      } catch (error) {
        console.warn(`⚠️ [BACKTEST] Failed to complete session:`, error);
      }
      this.currentSessionId = null;
    }

    // Exit backtest mode
    const paperStore = usePaperTradingStore.getState();
    paperStore.endBacktestSession();
    console.log(`🏁 [BACKTEST] Exited backtest mode`);
  }

  /**
   * Set playback speed (candles per second)
   */
  setSpeed(speed: PlaybackSpeed): void {
    const wasPlaying = this.state === 'playing';

    if (wasPlaying) {
      this.pause();
    }

    this.speed = speed;
    console.log(`⚡ [BACKTEST] Speed set to ${speed}x`);

    if (wasPlaying) {
      this.play();
    }
  }

  /**
   * Rebuild candles from ticks up to current index
   * Optimized to process in chunks to avoid freezing the UI
   */
  private async rebuildCandlesUpToIndex(): Promise<void> {
    const marketStore = useMarketStore.getState();
    const ticksUpToNow = this.allTicks.slice(0, this.currentIndex + 1);

    console.log(`🔄 [BACKTEST] Rebuilding ${ticksUpToNow.length.toLocaleString()} ticks into candles...`);

    // Build candles from these ticks using a Map for O(1) lookups
    const candlesMap = new Map<number, Candle>();
    const intervalMs = this.barInterval * 60 * 1000;
    const CHUNK_SIZE = 50000; // Process 50k ticks per chunk to avoid UI freeze
    let currentCandle: Candle | null = null;
    let currentBarTimestamp = 0;

    // Process ticks in chunks with yielding to prevent UI freeze
    for (let chunkStart = 0; chunkStart < ticksUpToNow.length; chunkStart += CHUNK_SIZE) {
      const chunkEnd = Math.min(chunkStart + CHUNK_SIZE, ticksUpToNow.length);
      const chunk = ticksUpToNow.slice(chunkStart, chunkEnd);

      for (const tick of chunk) {
        const barTimestamp = Math.floor(tick.timestamp / intervalMs) * intervalMs;

        if (!currentCandle || barTimestamp !== currentBarTimestamp) {
          // Save previous candle
          if (currentCandle) {
            candlesMap.set(currentBarTimestamp, currentCandle);
          }

          // Start new candle
          currentCandle = {
            timestamp: barTimestamp,
            open: tick.price,
            high: tick.price,
            low: tick.price,
            close: tick.price,
            volume: tick.volume
          };
          currentBarTimestamp = barTimestamp;
        } else {
          // Update current candle
          currentCandle.high = Math.max(currentCandle.high, tick.price);
          currentCandle.low = Math.min(currentCandle.low, tick.price);
          currentCandle.close = tick.price;
          currentCandle.volume += tick.volume;
        }
      }

      // Yield to UI every chunk to prevent freeze
      if (chunkEnd < ticksUpToNow.length) {
        await new Promise(resolve => setTimeout(resolve, 0));
      }
    }

    // Add the last candle
    if (currentCandle) {
      candlesMap.set(currentBarTimestamp, currentCandle);
    }

    // Update the current candle state for resuming playback
    // This is critical: when playback resumes after restoration, feedNextCandle()
    // needs to know what candle it's currently building
    this.currentCandle = currentCandle;
    this.currentBarTimestamp = currentBarTimestamp;

    // Convert map to sorted array
    const candles = Array.from(candlesMap.values()).sort((a, b) => a.timestamp - b.timestamp);

    // Store candles for component remounting
    this.lastGeneratedCandles = candles;

    // Set candles on chart
    marketStore.setCandles(candles);

    // Update current price and notify progress
    if (ticksUpToNow.length > 0) {
      const lastTick = ticksUpToNow[ticksUpToNow.length - 1];
      marketStore.setCurrentPrice(lastTick.price);

      // Update market status based on tick timestamp
      const tickDate = new Date(lastTick.timestamp);
      const marketOpen = isMarketOpen(tickDate);
      marketStore.setMarketOpen(marketOpen);
      marketStore.setMarketStatus(marketOpen ? 'OPEN' : 'CLOSED');

      this.callbacks.onProgress?.(
        this.currentIndex,
        this.allTicks.length,
        lastTick.timestamp
      );
    }

    console.log(`✅ [BACKTEST] Rebuilt ${candles.length} candles from ${ticksUpToNow.length.toLocaleString()} ticks`);
  }

  /**
   * Seek to specific candle index
   */
  async seekToIndex(index: number): Promise<void> {
    if (index < 0 || index >= this.allTicks.length) {
      console.warn(`Invalid seek index: ${index}`);
      return;
    }

    const wasPlaying = this.state === 'playing';

    if (wasPlaying) {
      this.pause();
    }

    this.currentIndex = index;
    console.log(`⏩ [BACKTEST] Seeked to index ${index}/${this.allTicks.length}`);

    // Save seekbar position to preferences for restoration across tab switches
    updatePreferenceDirect('backtestSeekbarIndex', index);

    await this.rebuildCandlesUpToIndex();

    if (wasPlaying) {
      this.play();
    }
  }

  /**
   * Seek to specific timestamp
   */
  async seekToTimestamp(timestamp: number): Promise<void> {
    console.log(`🔍 [SEEK] Looking for timestamp:`, new Date(timestamp).toISOString(), `(${timestamp})`);
    console.log(`🔍 [SEEK] Total ticks available:`, this.allTicks.length);

    if (this.allTicks.length === 0) {
      console.warn('❌ [SEEK] No ticks loaded, cannot seek');
      return;
    }

    // Log first and last tick timestamps for debugging
    console.log(`🔍 [SEEK] First tick:`, new Date(this.allTicks[0].timestamp).toISOString());
    console.log(`🔍 [SEEK] Last tick:`, new Date(this.allTicks[this.allTicks.length - 1].timestamp).toISOString());

    // Try to find exact timestamp or next available
    let index = this.allTicks.findIndex(c => c.timestamp >= timestamp);
    console.log(`🔍 [SEEK] Found index:`, index);

    if (index !== -1) {
      console.log(`✅ [SEEK] Seeking to index ${index} (timestamp: ${new Date(this.allTicks[index].timestamp).toISOString()})`);
      await this.seekToIndex(index);
    } else {
      // Timestamp is after the last available tick, seek to the last tick (floor)
      const lastIndex = this.allTicks.length - 1;
      console.log(`⚠️ [SEEK] Timestamp is beyond available data, seeking to last tick at index ${lastIndex} (timestamp: ${new Date(this.allTicks[lastIndex].timestamp).toISOString()})`);
      await this.seekToIndex(lastIndex);
    }
  }

  /**
   * Step forward one candle
   */
  stepForward(): void {
    if (this.currentIndex >= this.allTicks.length - 1) {
      console.warn('Already at last candle');
      return;
    }

    this.currentIndex++;
    this.feedNextCandle();
  }

  /**
   * Step backward one candle
   */
  async stepBackward(): Promise<void> {
    if (this.currentIndex <= 0) {
      console.warn('Already at first candle');
      return;
    }

    this.currentIndex--;
    await this.rebuildCandlesUpToIndex();
  }

  /**
   * Get current state
   */
  getState(): PlaybackState {
    return this.state;
  }

  /**
   * Get current progress
   */
  getProgress(): { current: number; total: number; percentage: number } {
    return {
      current: this.currentIndex,
      total: this.allTicks.length,
      percentage: (this.currentIndex / this.allTicks.length) * 100
    };
  }

  /**
   * Get current timestamp
   */
  getCurrentTimestamp(): number | null {
    if (this.currentIndex >= this.allTicks.length) {
      return null;
    }
    return this.allTicks[this.currentIndex]?.timestamp || null;
  }

  /**
   * Get timestamp at a specific index (for seekbar tooltip)
   */
  getTimestampAtIndex(index: number): number | null {
    if (index < 0 || index >= this.allTicks.length) {
      return null;
    }
    return this.allTicks[index]?.timestamp || null;
  }

  /**
   * Get start timestamp of backtest data
   */
  getStartTimestamp(): number | null {
    if (this.allTicks.length === 0) {
      return null;
    }
    return this.allTicks[0]?.timestamp || null;
  }

  /**
   * Get end timestamp of backtest data
   */
  getEndTimestamp(): number | null {
    if (this.allTicks.length === 0) {
      return null;
    }
    return this.allTicks[this.allTicks.length - 1]?.timestamp || null;
  }

  /**
   * Get the last generated candles (for component remounting)
   */
  getCurrentCandles(): Candle[] {
    return this.lastGeneratedCandles;
  }

  /**
   * Find the tick index closest to a given timestamp (in seconds)
   * Used for syncing seekbar position with chart visible range
   */
  findIndexByTimestamp(targetTimestamp: number): number {
    if (this.allTicks.length === 0) {
      return -1;
    }

    // Binary search for the closest timestamp
    let left = 0;
    let right = this.allTicks.length - 1;
    let closestIndex = 0;
    let closestDiff = Math.abs(this.allTicks[0].timestamp / 1000 - targetTimestamp);

    while (left <= right) {
      const mid = Math.floor((left + right) / 2);
      const tickTimestamp = this.allTicks[mid].timestamp / 1000; // Convert ms to seconds
      const diff = Math.abs(tickTimestamp - targetTimestamp);

      if (diff < closestDiff) {
        closestDiff = diff;
        closestIndex = mid;
      }

      if (tickTimestamp < targetTimestamp) {
        left = mid + 1;
      } else if (tickTimestamp > targetTimestamp) {
        right = mid - 1;
      } else {
        return mid; // Exact match
      }
    }

    return closestIndex;
  }

  /**
   * Start the playback interval
   */
  private startPlayback(): void {
    // For all speeds, use batching to maximize performance
    // Keep interval at a reasonable minimum (16ms ~= 60fps) and increase ticks per batch
    const MIN_INTERVAL_MS = 16; // ~60fps (62.5 batches per second)
    const MAX_TICKS_PER_BATCH = 100000; // Process up to 100k ticks per batch for ultra-high speeds

    let intervalMs: number;
    let ticksPerBatch: number;

    // Always use batching approach for consistent performance
    // At 16ms intervals, we get 62.5 batches/second (1000/16)
    // To achieve "speed" ticks/second: ticksPerBatch = speed / batchesPerSecond
    // ticksPerBatch = speed / (1000 / intervalMs) = (speed * intervalMs) / 1000
    intervalMs = MIN_INTERVAL_MS;
    const batchesPerSecond = 1000 / intervalMs; // 62.5 batches/sec
    ticksPerBatch = Math.max(1, Math.min(
      Math.ceil(this.speed / batchesPerSecond), // Use ceil to never go slower than requested
      MAX_TICKS_PER_BATCH
    ));

    console.log(`⚡ [BACKTEST] Playback config: ${intervalMs.toFixed(1)}ms interval, ${ticksPerBatch} ticks/batch (${this.speed}x speed)`);

    this.intervalId = setInterval(() => {
      // Process ticks in batch but throttle UI updates for high speeds
      const startIndex = this.currentIndex;

      for (let i = 0; i < ticksPerBatch && this.currentIndex < this.allTicks.length; i++) {
        // Skip UI updates for all but the last tick in the batch to prevent overwhelming React
        this.skipUIUpdates = (i < ticksPerBatch - 1);
        this.feedNextCandle();
      }

      // Final UI update for the batch (only if we processed at least one tick)
      const ticksProcessed = this.currentIndex - startIndex;
      if (ticksProcessed > 0 && this.currentIndex < this.allTicks.length) {
        const lastTick = this.allTicks[this.currentIndex - 1];
        const marketStore = useMarketStore.getState();

        // Force final chart update with the last processed candle
        if (this.currentCandle) {
          marketStore.updateOrAddCandle(this.currentCandle);
        }
        marketStore.setCurrentPrice(lastTick.price);

        // Update market status based on last tick timestamp
        const tickDate = new Date(lastTick.timestamp);
        const marketOpen = isMarketOpen(tickDate);
        marketStore.setMarketOpen(marketOpen);
        marketStore.setMarketStatus(marketOpen ? 'OPEN' : 'CLOSED');

        this.callbacks.onProgress?.(
          this.currentIndex,
          this.allTicks.length,
          lastTick.timestamp
        );
      }
    }, intervalMs);
  }

  /**
   * Feed the next tick and build candle in real-time
   */
  private feedNextCandle(): void {
    if (this.currentIndex >= this.allTicks.length) {
      this.complete();
      return;
    }

    try {
      const tick = this.allTicks[this.currentIndex];

      // Validate tick before processing
      if (!tick || typeof tick.timestamp !== 'number' || isNaN(tick.timestamp)) {
        console.error(`❌ [BACKTEST] Invalid tick at index ${this.currentIndex}:`, tick);
        this.currentIndex++;
        return;
      }

      const marketStore = useMarketStore.getState();

      // Calculate bar timestamp (aligned to bar interval)
      const intervalMs = this.barInterval * 60 * 1000;
      const barTimestamp = Math.floor(tick.timestamp / intervalMs) * intervalMs;

      // Log first few candles for debugging
      if (this.currentIndex < 5) {
        console.log(`🔍 [BACKTEST] Tick ${this.currentIndex}:`, {
          tickTime: tick.timestamp,
          tickTimeDate: new Date(tick.timestamp).toISOString(),
          barTimestamp,
          barTimestampDate: new Date(barTimestamp).toISOString(),
          price: tick.price,
          volume: tick.volume
        });
      }

      // Check if we need to start a new candle
      if (!this.currentCandle || barTimestamp !== this.currentBarTimestamp) {
        // Save the previous candle if it exists
        if (this.currentCandle) {
          if (this.currentIndex < 5) {
            console.log(`📊 [BACKTEST] Completing candle:`, {
              timestamp: this.currentCandle.timestamp,
              timestampType: typeof this.currentCandle.timestamp,
              isDate: (this.currentCandle.timestamp as unknown) instanceof Date,
              candle: this.currentCandle
            });
          }
          // Only update chart if not skipping UI updates (for performance at high speeds)
          if (!this.skipUIUpdates) {
            marketStore.updateOrAddCandle(this.currentCandle);
          }
        }

        // Start new candle
        this.currentCandle = {
          timestamp: barTimestamp,
          open: tick.price,
          high: tick.price,
          low: tick.price,
          close: tick.price,
          volume: tick.volume
        };
        this.currentBarTimestamp = barTimestamp;

        if (this.currentIndex < 5) {
          console.log(`🆕 [BACKTEST] New candle started:`, {
            timestamp: this.currentCandle.timestamp,
            timestampType: typeof this.currentCandle.timestamp,
            candle: this.currentCandle
          });
        }
      } else {
        // Update current candle with this tick
        this.currentCandle.high = Math.max(this.currentCandle.high, tick.price);
        this.currentCandle.low = Math.min(this.currentCandle.low, tick.price);
        this.currentCandle.close = tick.price;
        this.currentCandle.volume += tick.volume;

        // Update the current candle in the store (builds the candle tick-by-tick)
        // Skip chart updates when processing batch to prevent UI freeze
        if (!this.skipUIUpdates) {
          marketStore.updateOrAddCandle(this.currentCandle);
        }
      }

      // Update current price and notify progress (skip when batching for performance)
      if (!this.skipUIUpdates) {
        marketStore.setCurrentPrice(tick.price);

        // Update market status based on tick timestamp
        const tickDate = new Date(tick.timestamp);
        const marketOpen = isMarketOpen(tickDate);
        marketStore.setMarketOpen(marketOpen);
        marketStore.setMarketStatus(marketOpen ? 'OPEN' : 'CLOSED');

        this.callbacks.onProgress?.(
          this.currentIndex,
          this.allTicks.length,
          tick.timestamp
        );
      }

      this.currentIndex++;
    } catch (error) {
      console.error(`❌ [BACKTEST] Error feeding tick ${this.currentIndex}:`, error);
      this.pause();
      throw error;
    }
  }

  /**
   * Complete playback
   */
  private complete(): void {
    this.state = 'completed';
    updatePreferenceDirect('backtestPlaybackState', this.state);
    this.callbacks.onStateChange?.(this.state);
    this.callbacks.onComplete?.();
    console.log(`✅ [BACKTEST] Playback completed`);

    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }
}

// Singleton instance
let backtestPlayerInstance: BacktestPlayer | null = null;

export const getBacktestPlayer = (callbacks?: BacktestPlayerCallbacks): BacktestPlayer => {
  if (!backtestPlayerInstance) {
    backtestPlayerInstance = new BacktestPlayer(callbacks);
  } else if (callbacks) {
    // Update callbacks if provided
    backtestPlayerInstance.setCallbacks(callbacks);
  }
  return backtestPlayerInstance;
};
